import React, { forwardRef } from "react";

export interface ChipProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "filled" | "outlined" | "soft";
  color?: "default" | "primary" | "success" | "warning" | "error" | "info";
  size?: "sm" | "md" | "lg";
  onDelete?: () => void;
  /** Alias for onDelete (Material UI compatibility) */
  onClose?: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
  /** Alias for children (Material UI compatibility) */
  label?: React.ReactNode;
}

/**
 * Arc-style Chip/Badge component
 * 
 * Features:
 * - Multiple variants: filled, outlined, soft
 * - Multiple colors: default, primary, success, warning, error, info
 * - Multiple sizes: sm, md, lg
 * - Optional delete button
 * - Optional leading icon
 * - Smooth transitions
 */
const Chip = forwardRef<HTMLDivElement, ChipProps>(
  (
    {
      variant = "soft",
      color = "default",
      size = "md",
      onDelete,
      onClose,
      icon,
      disabled = false,
      children,
      label,
      className = "",
      ...props
    },
    ref
  ) => {
    // Support both onDelete and onClose (alias)
    const handleDelete = onDelete ?? onClose;
    // Support both children and label (alias)
    const content = children ?? label;
    // Base styles
    const baseStyles = `
      inline-flex items-center justify-center
      font-medium
      rounded-arc-full
      transition-all duration-arc-fast ease-arc
      select-none
    `;

    // Size styles
    const sizeStyles = {
      sm: "px-2 py-0.5 text-xs gap-1",
      md: "px-3 py-1 text-sm gap-1.5",
      lg: "px-4 py-1.5 text-sm gap-2",
    };

    // Color configurations
    const colorConfigs = {
      default: {
        filled: "bg-content-secondary text-white",
        outlined: "border border-arc-border text-content-primary bg-transparent",
        soft: "bg-surface-tertiary text-content-primary",
      },
      primary: {
        filled: "bg-accent text-white",
        outlined: "border border-accent text-accent bg-transparent",
        soft: "bg-accent-muted text-accent-dark",
      },
      success: {
        filled: "bg-success text-white",
        outlined: "border border-success text-success-dark bg-transparent",
        soft: "bg-success-light text-success-dark",
      },
      warning: {
        filled: "bg-warning-dark text-white",
        outlined: "border border-warning-dark text-warning-dark bg-transparent",
        soft: "bg-warning-light text-warning-dark",
      },
      error: {
        filled: "bg-error text-white",
        outlined: "border border-error text-error bg-transparent",
        soft: "bg-error-light text-error-dark",
      },
      info: {
        filled: "bg-info text-white",
        outlined: "border border-info text-info bg-transparent",
        soft: "bg-info-light text-info-dark",
      },
    };

    // Fallback to default color if an invalid color is passed
    const colorConfig = colorConfigs[color] ?? colorConfigs.default;
    const colorStyles = colorConfig[variant];

    // Delete button
    const DeleteButton = () => (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          handleDelete?.();
        }}
        className="ml-1 -mr-1 p-0.5 rounded-full hover:bg-black/10 transition-colors duration-arc-fast"
        aria-label="Remove"
      >
        <svg
          className="w-3 h-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    );

    return (
      <div
        ref={ref}
        className={`
          ${baseStyles}
          ${sizeStyles[size]}
          ${colorStyles}
          ${disabled ? "opacity-50 cursor-not-allowed" : ""}
          ${className}
        `}
        {...props}
      >
        {icon && <span className="flex-shrink-0">{icon}</span>}
        {content}
        {handleDelete && !disabled && <DeleteButton />}
      </div>
    );
  }
);

Chip.displayName = "Chip";

export { Chip };
export default Chip;
